// Act II scenario content — 협곡(속삭이는 협곡). Stages 4–6: 토르 → 니아 →
// 칼데르. Mirrors act1.ts. Dialogue text is verbatim from
// othellos/CAMPAIGN_DIALOGUE_REWRITE.md §4–6. Every narration/dialogue string
// is a `Loc`; names stay Korean (see act1.ts's comment / ./loc.ts).

import type { DialogueLine, PinNode, CompanionTurnLines } from "./act1.ts";
import { RIN_PORTRAIT, SERA_PORTRAIT } from "./act1.ts";
import type { Loc } from "./loc.ts";

export const ACT2_NAME: Loc = { ko: "II. 동료의 길", en: "II. The Allies' Path" };

export const TOR_ID = 4;
export const NIA_ID = 5;
export const CALDER_ID = 6;

/** Character illustration + scene/world-map background paths (public/assets/…). */
export const TOR_PORTRAIT = "/assets/characters/04-tor-wind-tracker-rival-v1.webp";
export const NIA_PORTRAIT = "/assets/characters/05-nia-glass-seer-rival-v1.webp";
export const CALDER_PORTRAIT =
  "/assets/characters/06-calder-gorge-twinblades-rival-v1.webp";
export const ACT2_BG = "/assets/backgrounds/act-2-whisper-gorge-v1.webp";
export const ACT2_MAP_BG = "/assets/world-maps/act-2-whisper-gorge.webp";

/** Act III world-map background — the 달빛 폐허 (moonlit ruins). Used as the
 *  backdrop for the 2막→3막 bridge subtitle and, later, the Act III map. */
export const ACT3_MAP_BG = "/assets/world-maps/act-3-moon-ruins.webp";

/** ACT2_TO_ACT3 — bridge narration after 칼데르's gate opens: the party passes
 *  through, 칼데르 stays, they reach the ruined guardian hall and find the seal
 *  lore carved there with its final line missing. Mirrors ACT1_TO_ACT2_LINES:
 *  recall (past) → arrival (past) → the standing fact (present) → the hook
 *  (present). `lastHint` points on to 오르나. */
export const ACT2_TO_ACT3_LINES: readonly Loc[] = [
  { ko: "칼데르는 관문을 열고, 뒷일을 떠안은 채 협곡에 남았다.", en: "Calder opened the gate and stayed behind in the gorge, shouldering what came after." },
  { ko: "관문 너머, 옛 수호단의 폐허가 달빛에 잠겨 있었다.", en: "Beyond the gate, the ruins of an old guardian order lay steeped in moonlight." },
  { ko: "봉인을 되돌리는 법이 이 폐허의 비문에 새겨져 있다.", en: "The way to restore the seal is carved into this ruin's inscription." },
  { ko: "그 비문의 마지막 한 줄이 비어 있다.", en: "The inscription's last line is missing." },
];

/** ACT2_MAP — three pact-stone nodes up the gorge stream, near to far: 토르 by
 *  the stream mouth, 니아 at the mid bend, 칼데르 at the far gorge gate. */
export const ACT2_PINS: readonly PinNode[] = [
  { id: 4, name: "토르", title: "바람길 추적자", pos: { x: 0.44, y: 0.82 }, scale: 1.2 },
  { id: 5, name: "니아", title: "유리 구슬 점술가", pos: { x: 0.732, y: 0.664 }, scale: 1 },
  { id: 6, name: "칼데르", title: "협곡의 쌍검", pos: { x: 0.408, y: 0.587 }, scale: 0.72 },
];

/** STAGE4_TOR_MEET — 토르 reads the split red trail, then distrusts the
 *  stranger; 린 is worn out from re-explaining, 세라 vouches for the player's
 *  play and calls for the trial. 린·세라 travel with the party now. */
export const TOR_MEET: readonly DialogueLine[] = [
  { speaker: "opp", name: "토르", text: { ko: "붉은 흔적이 둘로 갈라졌다. 하나는 적이 일부러 만든 가짜 길이야.", en: "The red trail splits in two. One's a false path the enemy laid on purpose." } },
  { speaker: "opp", name: "세라", text: { ko: "그래서 우리를 기다렸구나.", en: "So that's why you were waiting for us." } },
  { speaker: "opp", name: "토르", text: { ko: "처음 보는 얼굴이군. 당신은 누구지?", en: "A face I don't know. Who are you?" } },
  { speaker: "opp", name: "린", text: { ko: "이 사람은 봉인을 되돌리려… 하… 몇 번째 말해야 하는 거지…", en: "This one's here to restore the seal... ugh... how many times do I have to say it..." } },
  { speaker: "opp", name: "토르", text: { ko: "처음 보는 자를 믿을 수는 없지.", en: "I can't trust someone I've never met." } },
  { speaker: "opp", name: "세라", text: { ko: "쓸 만한 실력이야. 시험해 봐.", en: "Their skill's solid. Test them." } },
];

/** STAGE4_TOR_OUTRO — after the win: 토르 is convinced, 브람 (the explorer)
 *  picks the path with his usual hedging, 토르 names it and the urgency. */
export const TOR_OUTRO: readonly DialogueLine[] = [
  { speaker: "opp", name: "토르", text: { ko: "세라 말이 맞았군. 쓸 만한 실력이야.", en: "Sera was right. Solid skill." } },
  { speaker: "player", name: "나", text: { ko: "그런데 어느 쪽이 진짜 길이지?", en: "So which path is the real one?" } },
  { speaker: "opp", name: "브람", text: { ko: "길 찾기라면 내가 나서야지. 흠… 이쪽이 확실해. …아마도.", en: "Pathfinding's my job. Hm... this way's the one. ...Probably." } },
  { speaker: "opp", name: "토르", text: { ko: "그쪽은 유리 갈림길. 니아가 있는 곳이군.", en: "That's the Glass Fork. Where Nia is." } },
  { speaker: "opp", name: "토르", text: { ko: "적이 니아의 예지 능력을 빼앗으려 한다. 늦기 전에 가야 해.", en: "The enemy's trying to seize Nia's foresight. We need to get there before it's too late." } },
];

/** STAGE5_NIA_MEET — the party arrives on 토르's warning to find 니아's scrying
 *  orb cracking with a red rift, stuck on one deadly vision; the rite duel
 *  pushes that false future back out. 토르 (who knows her) greets, 브람 reframes. */
export const NIA_MEET: readonly DialogueLine[] = [
  { speaker: "opp", name: "토르", text: { ko: "니아! 아직 무사하구나.", en: "Nia! You're still safe." } },
  { speaker: "opp", name: "니아", text: { ko: "토르… 왔구나. 구슬에 붉은 균열이 번지고 있어. 이제 한 가지 미래밖에 안 보여 — 우리가 전부 무너지는 장면.", en: "Tor... you came. Red rift-cracks are spreading through the orb. It only shows one future now — all of us falling." } },
  { speaker: "opp", name: "브람", text: { ko: "그건 진짜 미래가 아니야. 균열이 구슬에 억지로 새겨 넣은 거짓 미래지.", en: "That's not a real future. It's a false one the rift is forcing into the orb." } },
  { speaker: "opp", name: "니아", text: { ko: "이 거짓 미래를 밀어내려면, 누군가 나와 의식 결투를 끝까지 도와 줘야 해.", en: "To push this false future back out, someone has to see the rite duel through with me." } },
  { speaker: "player", name: "나", text: { ko: "내가 하지. 진짜 미래를 되찾자.", en: "I'll do it. Let's take the real future back." } },
];

/** STAGE5_NIA_OUTRO — the crack is gone; 니아 stays behind to watch the party's
 *  back through the orb, 토르 takes her up on it. Points on to 칼데르's gate. */
export const NIA_OUTRO: readonly DialogueLine[] = [
  { speaker: "opp", name: "니아", text: { ko: "구슬에 균열이 사라졌어. 구슬에 다시 여러 갈래 길이 보여.", en: "The crack in the orb is gone. It shows many branching paths again." } },
  { speaker: "opp", name: "니아", text: { ko: "그 길들 끝에 문이 하나 있어. 협곡 관문 — 침공로의 마지막 문이야.", en: "At the end of those paths is a gate. The Gorge Gate — the last door on the invasion route." } },
  { speaker: "opp", name: "니아", text: { ko: "여는 열쇠는 칼데르가 쥐고 있어. 나는 여기 남아 구슬로 너희 뒤를 지켜볼게.", en: "Calder holds the key to open it. I'll stay here and watch your backs through the orb." } },
  { speaker: "opp", name: "토르", text: { ko: "고맙다. 앞은 우리가 연다. 갈림길에 함정이 보이면 지체 없이 알려 줘.", en: "Thanks. We'll open the way ahead. If you see a trap at the fork, tell us at once." } },
];

/** STAGE5 turn bar — companion-voiced like 세라's stage. The duel is the rite
 *  that rewrites the one bad future 니아's orb is stuck on, so 니아 leads the
 *  player-turn line and 세라 takes the "behind" nudge. 린 gives a one-shot
 *  cheer at move 16 (`STAGE_5.companion.rinAtMove`). No 브람 line, no firstTurn
 *  (moveNo≤1 falls back to `turn`). 니아's own turn keeps `voice.thinking`. */
export const NIA_TURN_LINES: CompanionTurnLines = {
  turn: { name: "니아", text: { ko: "너의 선택으로 미래를 바꿀 수 있어.", en: "Your choice can change the future." } },
  slow: { name: "니아", text: { ko: "미래가 다시 흐려지고 있어. 서둘러.", en: "The future's clouding again. Hurry." } },
  behind: { name: "세라", text: { ko: "아직 미래를 바꿀 기회는 있어. 모서리를 잡아.", en: "There's still a chance to change the future. Take the corners." } },
  rin: { name: "린", text: { ko: "니아가 본 그 미래, 우리 손으로 바꾸는거야.", en: "That future Nia saw — we're the ones changing it." } },
};

/** Speaker name → hero illustration while that companion voices the turn bar. */
export const NIA_COMPANION_PORTRAITS: Record<string, string> = {
  니아: NIA_PORTRAIT,
  세라: SERA_PORTRAIT,
  린: RIN_PORTRAIT,
};

/** STAGE6_CALDER_MEET — the party reaches the gorge gate; 칼데르 halts them, the
 *  gate is sealed by 명령, and the only path it permits is a rite duel with the
 *  expedition's representative. 토르 presses the urgency, 세라 names the rep. */
export const CALDER_MEET: readonly DialogueLine[] = [
  { speaker: "opp", name: "칼데르", text: { ko: "멈춰라. 이 관문은 명령으로 봉해졌다. 누구에게도 열 수 없다.", en: "Halt. This gate is sealed by order. It opens for no one." } },
  { speaker: "opp", name: "토르", text: { ko: "칼데르, 균열군이 이미 수로를 넘었다. 지체할 시간이 없어.", en: "Calder, the rift army's already crossed the waterways. There's no time to waste." } },
  { speaker: "opp", name: "칼데르", text: { ko: "안다. 그러나 명령을 거부할 수는 없어. 길은 하나뿐 — 나와 결투해서 이겨라.", en: "I know. But I can't defy the order. There's one way — duel me, and win." } },
  { speaker: "opp", name: "세라", text: { ko: "대표는 저 사람이야. 문 열 자격이 있다는 걸 직접 보여 줘.", en: "That one's our representative. Show them yourself you've earned the right to open the door." } },
  { speaker: "player", name: "나", text: { ko: "그 자격, 내가 증명하지.", en: "I'll prove I've earned it." } },
];

/** STAGE6_CALDER_OUTRO — the duel is won; 칼데르 opens the gate despite the order
 *  and takes the fallout on himself. 린 thanks him, 브람 names what waits in the
 *  ruins, 칼데르 points the party to 오르나 as its keeper. */
export const CALDER_OUTRO: readonly DialogueLine[] = [
  { speaker: "opp", name: "칼데르", text: { ko: "결투는 끝났다. 관문을 열겠다.", en: "The duel's over. I'll open the gate." } },
  { speaker: "opp", name: "토르", text: { ko: "명령 위반인데, 괜찮겠어?", en: "That breaks the order. You sure you're okay with that?" } },
  { speaker: "opp", name: "칼데르", text: { ko: "뒷일은 내가 책임진다.", en: "I'll answer for what comes after." } },
  { speaker: "opp", name: "린", text: { ko: "고마워요, 칼데르.", en: "Thank you, Calder." } },
  { speaker: "opp", name: "브람", text: { ko: "유적에 봉인을 되돌리는 법이 적혀 있어.", en: "The ruins hold how to restore the seal." } },
  { speaker: "opp", name: "칼데르", text: { ko: "오르나를 찾아라. 그가 기록을 지키고 있다.", en: "Find Orna. She keeps the records." } },
];
